import { type FC, useState } from 'react'
import { Form } from 'react-bootstrap'
import classNames from 'classnames'
import { Button } from '../../../components/Button/Button'
import { type AvailabilitySchedule, type DayOfWeek, type TimeInterval } from '../../../models/Scheduling'
import {
  endOptionsFor,
  formatTimeOptionLabel,
  nextFreeInterval,
  sortIntervals,
  startOptionsFor,
  type TimeOption,
  WEEKDAY_LABELS,
  WEEKDAY_ORDER,
} from './availabilityOptions'
import styles from './DefaultAvailabilityEditor.module.scss'

const READONLY_VISIBLE_INTERVALS = 2

interface DefaultAvailabilityEditorProps {
  availability: AvailabilitySchedule
  readOnly?: boolean
  onAvailabilityChange: (availability: AvailabilitySchedule) => void
}

const WeekdayAvatar: FC<{ day: DayOfWeek; active: boolean }> = ({ day, active }) => (
  <div
    className={classNames(
      styles.avatar,
      'd-flex align-items-center justify-content-center rounded-circle flex-shrink-0 bold',
      active ? 'zocks-bg-color-primary zocks-text-white' : 'zocks-bg-color-gray-200 zocks-text-gray-600',
    )}
  >
    {WEEKDAY_LABELS[day][0]}
  </div>
)

const ReadOnlyDayRow: FC<{ day: DayOfWeek; intervals: TimeInterval[] }> = ({ day, intervals }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const visible = isExpanded ? intervals : intervals.slice(0, READONLY_VISIBLE_INTERVALS)
  const hiddenCount = intervals.length - visible.length
  return (
    <div className={classNames('d-flex zocks-gap-0.75 py-1', intervals.length <= 1 ? 'align-items-center' : 'align-items-start')}>
      <WeekdayAvatar day={day} active={intervals.length > 0} />
      {intervals.length === 0 ? (
        <span className='paragraph-medium zocks-text-gray-500'>Unavailable</span>
      ) : (
        <div className='d-flex flex-column zocks-gap-0.125'>
          <div className='d-flex flex-column'>
            {visible.map((interval) => (
              <span key={`${interval.start}-${interval.end}`} className='paragraph-medium zocks-text-gray-900'>
                {formatTimeOptionLabel(interval.start).toLowerCase()} – {formatTimeOptionLabel(interval.end).toLowerCase()}
              </span>
            ))}
          </div>
          {hiddenCount > 0 || isExpanded ? (
            <Button type='button' className='align-self-start p-0 zocks-gap-0.25 paragraph-small zocks-text-primary' onClick={() => setIsExpanded(!isExpanded)}>
              <i className={isExpanded ? 'bi bi-chevron-up' : 'bi bi-chevron-down'} />
              {isExpanded ? 'Show less' : `Show ${hiddenCount} more`}
            </Button>
          ) : null}
        </div>
      )}
    </div>
  )
}

const TimeSelect: FC<{ value: string; label: string; options: TimeOption[]; onChange: (value: string) => void }> = ({ value, label, options, onChange }) => (
  <Form.Select className={styles.timeSelect} value={value} aria-label={label} onChange={(event) => onChange(event.currentTarget.value)}>
    {options.map((option) => (
      <option key={option.value} value={option.value}>
        {option.label}
      </option>
    ))}
  </Form.Select>
)

export const DefaultAvailabilityEditor: FC<DefaultAvailabilityEditorProps> = ({ availability, readOnly = false, onAvailabilityChange }) => {
  const intervalsForDay = (day: DayOfWeek): TimeInterval[] =>
    sortIntervals(availability.days.find((dayAvailability) => dayAvailability.day === day)?.intervals ?? [])

  const setDay = (day: DayOfWeek, intervals: TimeInterval[]) => {
    const others = availability.days.filter((dayAvailability) => dayAvailability.day !== day)
    onAvailabilityChange({ days: intervals.length > 0 ? [...others, { day, intervals: sortIntervals(intervals) }] : others })
  }

  const updateInterval = (day: DayOfWeek, index: number, patch: Partial<TimeInterval>) => {
    setDay(
      day,
      intervalsForDay(day).map((interval, intervalIndex) => (intervalIndex === index ? { ...interval, ...patch } : interval)),
    )
  }

  const copyToAllDays = (intervals: TimeInterval[]) => {
    onAvailabilityChange({ days: WEEKDAY_ORDER.map((day) => ({ day, intervals: intervals.map((interval) => ({ ...interval })) })) })
  }

  if (readOnly) {
    return (
      <div className='d-flex flex-column zocks-gap-0.125'>
        {WEEKDAY_ORDER.map((day) => (
          <ReadOnlyDayRow key={day} day={day} intervals={intervalsForDay(day)} />
        ))}
      </div>
    )
  }

  return (
    <div className='d-flex flex-column'>
      {WEEKDAY_ORDER.map((day, index) => {
        const intervals = intervalsForDay(day)
        const newInterval = nextFreeInterval(intervals)
        return (
          <div
            key={day}
            className={classNames(
              'd-flex align-items-start gap-3 zocks-py-0.75',
              index < WEEKDAY_ORDER.length - 1 ? 'border-bottom zocks-border-color-gray-300' : null,
            )}
          >
            <WeekdayAvatar day={day} active={intervals.length > 0} />
            <div className='d-flex flex-column gap-2 flex-grow-1'>
              {intervals.length === 0 && newInterval ? (
                <div className='d-flex align-items-center gap-2'>
                  <span className='paragraph-small zocks-text-gray-600'>Unavailable</span>
                  <Button
                    type='button'
                    variant='borderlessGrayButton'
                    className='p-1'
                    aria-label={`Add ${WEEKDAY_LABELS[day]} range`}
                    onClick={() => setDay(day, [newInterval])}
                  >
                    <i className='bi bi-plus-circle' />
                  </Button>
                </div>
              ) : null}
              {intervals.map((interval, index) => (
                <div key={index} className='d-flex align-items-center gap-2'>
                  <TimeSelect
                    value={interval.start}
                    label={`${WEEKDAY_LABELS[day]} start time`}
                    options={startOptionsFor(intervals, index)}
                    onChange={(start) => updateInterval(day, index, { start })}
                  />
                  <span className='zocks-text-gray-600'>–</span>
                  <TimeSelect
                    value={interval.end}
                    label={`${WEEKDAY_LABELS[day]} end time`}
                    options={endOptionsFor(intervals, index)}
                    onChange={(end) => updateInterval(day, index, { end })}
                  />
                  <Button
                    type='button'
                    variant='borderlessGrayButton'
                    className='p-1'
                    aria-label={`Remove ${WEEKDAY_LABELS[day]} range`}
                    onClick={() =>
                      setDay(
                        day,
                        intervals.filter((_, intervalIndex) => intervalIndex !== index),
                      )
                    }
                  >
                    <i className='bi bi-trash' />
                  </Button>
                  {index === intervals.length - 1 && newInterval ? (
                    <Button
                      type='button'
                      variant='borderlessGrayButton'
                      className='p-1'
                      aria-label={`Add ${WEEKDAY_LABELS[day]} range`}
                      onClick={() => setDay(day, [...intervals, newInterval])}
                    >
                      <i className='bi bi-plus-circle' />
                    </Button>
                  ) : null}
                </div>
              ))}
            </div>
            {intervals.length > 0 ? (
              <div className='d-flex align-items-center gap-2'>
                <Button
                  type='button'
                  variant='borderlessGrayButton'
                  className='p-1'
                  aria-label={`Copy ${WEEKDAY_LABELS[day]} times to all days`}
                  onClick={() => copyToAllDays(intervals)}
                >
                  <i className='bi bi-copy' />
                </Button>
                <Button type='button' variant='grayButton' className='d-flex align-items-center px-3 py-2' onClick={() => setDay(day, [])}>
                  Clear day
                </Button>
              </div>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}
